// Some steps send the contractor to a third party that returns them to /client/documents?event=...
// in a fresh page load: DocuSign's "sign in a new tab" fallback, and the LMS course redirect. In the
// recollection flow the step to show lives only in Redux (recollection.selectedStepId), so the fresh
// page has no step selected and would redirect home, dropping the event. The step id is kept in
// localStorage (shared across tabs) so the returning page can restore it. Each source has its own
// key, so a stale DocuSign entry is never restored for an LMS return and vice versa.

export enum PendingReturnStepSource {
  Docusign = 'docusign',
  LMSTraining = 'lms_training',
}

export const LMS_TRAINING_COMPLETED_EVENT = 'lms_training_completed'

const PENDING_RETURN_STEP_STORAGE_KEYS: Record<PendingReturnStepSource, string> = {
  [PendingReturnStepSource.Docusign]: 'docusign_fallback_signing_pending',
  [PendingReturnStepSource.LMSTraining]: 'lms_training_redirect_pending',
}

const PENDING_RETURN_STEP_MAX_AGE_MS = 2 * 60 * 60 * 1000

interface PendingReturnStep {
  stepId: string
  startedAt: number
}

export const getPendingReturnStepSource = (returnEvent: string): PendingReturnStepSource => {
  if (returnEvent === LMS_TRAINING_COMPLETED_EVENT) {
    return PendingReturnStepSource.LMSTraining
  }

  return PendingReturnStepSource.Docusign
}

export const savePendingReturnStep = (source: PendingReturnStepSource, stepId: string) => {
  try {
    const pending: PendingReturnStep = { stepId, startedAt: Date.now() }

    localStorage.setItem(PENDING_RETURN_STEP_STORAGE_KEYS[source], JSON.stringify(pending))
  } catch {}
}

export const clearPendingReturnStep = (source: PendingReturnStepSource) => {
  try {
    localStorage.removeItem(PENDING_RETURN_STEP_STORAGE_KEYS[source])
  } catch {}
}

export const readPendingReturnStepId = (source: PendingReturnStepSource): string | null => {
  try {
    const raw = localStorage.getItem(PENDING_RETURN_STEP_STORAGE_KEYS[source])

    if (!raw) {
      return null
    }

    const pending = JSON.parse(raw) as PendingReturnStep

    if (Date.now() - pending.startedAt > PENDING_RETURN_STEP_MAX_AGE_MS) {
      clearPendingReturnStep(source)

      return null
    }

    return pending.stepId
  } catch {
    return null
  }
}
